/**
 * Copyright 2021-2023 (c) Karsten Röthig, FVA GmbH (for umati and VDW e.V.)
 */

import { UaType, UaVariable } from './UaVariableType';

export interface Template {
  header: Header;
  imagesConfig: ImagesConfig;
  stacklight?: Stacklight;
  timeline?: Timeline;
  sections: Section[];
}

export interface Header {
  nameShowStaticText?: boolean;
  nameVariable?: UaVariable;
  nameStaticText?: string;
  modelShowStaticText?: boolean;
  modelVariable?: UaVariable;
  modelStaticText?: string;
}

export interface ImagesConfig {
  defaultImagePathClassifierVariable?: UaVariable;
  imageDefaults: ImageDefaults;
}

export interface ImageDefaults {
  [key: string]: string;
}

export interface Stacklight {
  lampsPlaceholderObject?: string;
  signalVariable?: UaVariable;
  signalOnValue?: string;
  signalOffValue?: string;
  sortingVariable?: UaVariable;
  colorVariable?: UaVariable;
  colorValues: ColorValues;
}

export interface Timeline {
  variable?: UaVariable;
  colorValues: ColorValues;
}

export interface ColorValues {
  [key: string]: string;
}

export interface Section {
  rows: SectionRow[];
  isPlaceholder: boolean;
  // available if placeholder section :: START
  placeholderObject?: string;
  titleVariable?: UaVariable;
  // available if placeholder section :: END
  // available if not placeholder section :: START
  title?: string;
  // available if not placeholder section :: END
  condition?: RenderingCondition;
}

export interface SectionRow {
  elements: SectionRowElement[];
  isPlaceholder: boolean;
  // available if placeholder row :: START
  placeholderObject?: string;
  placeholderConditionType?: UaType;
  titleVariable?: UaVariable;
  // available if placeholder row :: END
  // available if not placeholder row :: START
  title?: string;
  // available if not placeholder row :: START
  condition?: RenderingCondition;
}

export enum SectionRowElementTypeEnum {
  TEXT,
  CHART,
  BADGE,
  TABLE,
  LIST,
}

export interface SectionRowElement {
  type: SectionRowElementTypeEnum;
  valueVariable?: UaVariable;
  customUnit?: string;
  isPlaceholder: boolean;
  // available if placeholder element :: START
  placeholderObject?: string;
  labelVariable?: UaVariable;
  // available if placeholder element :: END
  // available if not placeholder element :: START
  label?: string;
  // available if not placeholder element :: END
  condition?: RenderingCondition;
}

export interface SectionRowElementText extends SectionRowElement {
  showAsLink?: boolean;
}

export interface SectionRowElementChart extends SectionRowElement {
  customRangeHigh?: number;
  customRangeLow?: number;
  defaultColorGradient?: SectionRowElementChartColorGradient;
  conditionalColorGradients?: SectionRowElementChartColorGradientConditional[];
}

export interface SectionRowElementChartColorGradient {
  startColor: string;
  stopColor: string;
}

export interface SectionRowElementChartColorGradientConditional extends SectionRowElementChartColorGradient {
  comparisonValueVariable?: UaVariable;
  comparisonOperator?: SectionRowElementChartColorGradientConditionalOperatorType;
}

export type SectionRowElementChartColorGradientConditionalOperatorType =
  | 'greaterThan'
  | 'greaterThanOrEqualTo'
  | 'equalTo'
  | 'lessThanOrEqualTo'
  | 'lessThan';

export interface SectionRowElementBadge extends SectionRowElement {
  badgeValues: SectionRowElementBadgeValue[];
}

export interface SectionRowElementBadgeValue {
  backgroundColor: string;
  textColor: string;
  value: string;
  label?: string;
}

export interface SectionRowElementTable extends SectionRowElement {
  rowPlaceholderObject?: string;
  columns: SectionRowElementTableColumn[];
}

export interface SectionRowElementTableColumn {
  label: string;
  valueVariable?: UaVariable;
}

export interface SectionRowElementList extends SectionRowElementText {}

export interface RenderingCondition {
  valueVariable?: UaVariable;
  values: string[];
}
